// AgentHost: the one RPC between the resident agent and the core. The agent
// calls; the core answers and sends notifications. The offline edition
// carries the frames over a MessagePort or child_process IPC, the cloud over
// one WebSocket; both ends validate every call with the same zod schemas
// declared here.
//
// Frames:
//   agent -> core  { t: "call", id, method, params }
//   core -> agent  { t: "result", id, ok: true, value }
//                  { t: "result", id, ok: false, error: { kind, code, message } }
//                  { t: "notify", event }
//
// Notifications only say "look": the agent pulls the content with a call.
// Missing one is harmless, because after `hello` the agent calls
// `chats.pending` once and then waits for notifications.
import { z } from "zod";
import {
  AgentKind,
  Auth,
  Capabilities,
  Device,
  Login,
  LoginCommand,
} from "./capabilities";
import { signedShape } from "./instruction";
import {
  CHANGES_MAX,
  Change,
  ChatInfo,
  ChatResume,
  ChatSpec,
  Entry,
  FileRef,
  HistoryItem,
  REPLY_TEXT_MAX,
  Request,
  RequestResult,
  RunEnd,
  RunLive,
  TurnItem,
} from "./shapes";
import {
  SlackDraftResult,
  TURN_EDIT_CAUSES,
  TURN_EDIT_OUTCOMES,
  TURN_OPEN_MAX,
  TURN_POSITIONS,
  TurnAnswer,
  TurnSpec,
} from "./turn";

/** Version of this protocol, sent in `hello`. */
export const AGENT_HOST_PROTOCOL = 1;

/** Cloud: where an agent opens its one WebSocket (the device token goes in `Authorization`). */
export const AGENT_HOST_PATH = "/api/agent/host";

/**
 * Cloud: the close codes of the agent's WebSocket and what the agent does with
 * each. Any other close (a node stopping sends 1012, a cut network none) is
 * reconnected with backoff.
 */
export const AGENT_HOST_CLOSE = {
  /** A newer connection of the same device took over: exit, do not reconnect. */
  superseded: 4010,
  /** The device token was revoked or the device removed: exit, do not reconnect. */
  revoked: 4011,
  /** The host refused this protocol (`hello` failed with `upgrade_required`): exit to update. */
  upgradeRequired: 4012,
  /** The host is older than this protocol: reconnect, but only at the longest interval. */
  tryLater: 4013,
} as const;

// --- errors ---

/**
 * What kind of failure a call met. The agent decides by the kind, never by a
 * transport status:
 * - released: this device no longer holds the chat (forget the chat's work)
 * - gone: the chat, run, item or request no longer exists
 * - conflict: a revision or version is stale, or a limit was reached
 * - refused: not allowed (permissions, a run of another device)
 * - invalid: the params do not match the schema
 * - retry: temporary; call again later with backoff
 */
export const HOST_ERROR_KINDS = [
  "released",
  "gone",
  "conflict",
  "refused",
  "invalid",
  "retry",
] as const;
export type HostErrorKind = (typeof HOST_ERROR_KINDS)[number];

export const HostErrorBody = z.object({
  kind: z.enum(HOST_ERROR_KINDS),
  /** The core's error code (`turn_full`, `stale_revision`); material for text to the AI. */
  code: z.string().max(200),
  message: z.string().max(4000),
});
export type HostErrorBody = z.infer<typeof HostErrorBody>;

export class HostError extends Error {
  readonly kind: HostErrorKind;
  readonly code: string;

  constructor(kind: HostErrorKind, code: string, message: string) {
    super(message);
    this.name = "HostError";
    this.kind = kind;
    this.code = code;
  }

  toBody(): HostErrorBody {
    return { kind: this.kind, code: this.code, message: this.message };
  }
}

/**
 * The `refused` code of a host that will not take this device back (it was
 * removed, or another connection of it took over): hello is not tried again
 * on that connection.
 */
export const HOST_DEVICE_REMOVED = "device_removed";

export function isHostError(err: unknown): err is HostError {
  return err instanceof HostError;
}

/** The error body for anything thrown while handling a call. */
export function toHostErrorBody(err: unknown): HostErrorBody {
  if (err instanceof HostError) return err.toBody();
  if (err instanceof z.ZodError)
    return { kind: "invalid", code: "invalid_params", message: err.message };
  return {
    kind: "retry",
    code: "internal",
    message: err instanceof Error ? err.message : String(err),
  };
}

// --- frames ---

export const CallFrame = z.object({
  t: z.literal("call"),
  id: z.number().int().min(0),
  method: z.string().min(1).max(200),
  params: z.unknown(),
});
export type CallFrame = z.infer<typeof CallFrame>;

export const ResultFrame = z.union([
  z.object({
    t: z.literal("result"),
    id: z.number().int().min(0),
    ok: z.literal(true),
    value: z.unknown(),
  }),
  z.object({
    t: z.literal("result"),
    id: z.number().int().min(0),
    ok: z.literal(false),
    error: HostErrorBody,
  }),
]);
export type ResultFrame = z.infer<typeof ResultFrame>;

/**
 * The newest agent release a cloud host knows of. An agent under a supervisor
 * that runs another version exits to update (at once if urgent, else when idle).
 */
export const LatestRelease = z.object({
  version: z.string().min(1).max(100),
  urgent: z.boolean(),
});
export type LatestRelease = z.infer<typeof LatestRelease>;

/** What the core tells the agent. */
export const HostEvent = z.discriminatedUnion("kind", [
  /** A chat has new entries to read. */
  z.object({ kind: z.literal("wake"), chatId: z.string().min(1) }),
  /** A person's request about a run. */
  z.object({ kind: z.literal("request"), request: Request }),
  /** The device's settings changed. */
  z.object({ kind: z.literal("settings"), device: Device }),
  /** A login instruction from the screen. */
  z.object({
    kind: z.literal("login"),
    command: LoginCommand,
    signed: signedShape.nullable().optional(),
  }),
  /** Cloud only: a new agent release was published. */
  z.object({ kind: z.literal("update"), latest: LatestRelease }),
]);
export type HostEvent = z.infer<typeof HostEvent>;

export const NotifyFrame = z.object({
  t: z.literal("notify"),
  event: HostEvent,
});
export type NotifyFrame = z.infer<typeof NotifyFrame>;

export const Frame = z.union([CallFrame, ResultFrame, NotifyFrame]);
export type Frame = CallFrame | ResultFrame | NotifyFrame;

// --- methods ---

/** One method: its name and the schemas both ends validate with. */
export interface HostMethod<
  P extends z.ZodType = z.ZodType,
  R extends z.ZodType = z.ZodType,
> {
  readonly name: string;
  readonly params: P;
  readonly result: R;
}

/**
 * Declares a method. Groups that come later (the secretary's inputs) declare
 * theirs the same way and are merged into the table by name.
 */
export function defineHostMethod<
  const N extends string,
  P extends z.ZodType,
  R extends z.ZodType,
>(name: N, schemas: { params: P; result: R }): HostMethod<P, R> & { name: N } {
  return { name, params: schemas.params, result: schemas.result };
}

/** A table of methods keyed by name. */
export type HostMethodTable = Record<string, HostMethod>;

/** Builds a table from method declarations; a duplicate name is a bug. */
export function hostMethodTable<const M extends readonly HostMethod[]>(
  ...methods: M
): { [K in M[number] as K["name"]]: K } {
  const table: Record<string, HostMethod> = {};
  for (const m of methods) {
    if (table[m.name]) throw new Error(`duplicate host method ${m.name}`);
    table[m.name] = m;
  }
  return table as { [K in M[number] as K["name"]]: K };
}

const Ok = z.null();
const Id = z.string().min(1).max(200);
const ChatParams = z.object({ chatId: Id });

// start

export const hello = defineHostMethod("hello", {
  params: z.object({
    protocol: z.number().int().min(1),
    /** The agent's own version (shown on screen). */
    version: z.string().max(100),
    host: z.string().max(200),
    capabilities: Capabilities,
    /**
     * Runs this process still has going (a reconnect) or is about to settle
     * itself (runs an earlier process left, ended next with `interrupted`).
     * The core fails the device's other running runs and puts what they read
     * back to unread.
     */
    running: z.array(Id).max(1000).optional(),
  }),
  result: z.object({
    device: Device,
    owner: z.object({ id: Id, name: z.string().max(200) }),
    /** The owner's locale; text for people and for the AI follows it. */
    locale: z.string().max(20).nullable(),
    /** Cloud only: the newest agent release (absent offline, where the app updates as a whole). */
    latest: LatestRelease.nullable().optional(),
  }),
});

export const capabilitiesPut = defineHostMethod("capabilities.put", {
  params: Capabilities,
  result: Ok,
});

// receiving work

export const chatsPending = defineHostMethod("chats.pending", {
  params: z.object({}),
  result: z.object({
    /** Chats on this device with waking entries no run has read. */
    chats: z.array(Id).max(10_000),
    /** Requests not settled yet. */
    requests: z.array(Request).max(1000),
  }),
});

export const chatsWork = defineHostMethod("chats.work", {
  params: ChatParams,
  result: z.object({
    chat: ChatInfo,
    spec: ChatSpec,
    /** Waking entries after what the runs of this chat have read, oldest first. */
    inputs: z.array(Entry).max(1000),
    /** The CLI session to continue; null starts a new one. */
    resume: ChatResume.nullable(),
    /** The chat so far, when there is no session to continue but there is history. */
    history: z
      .object({ items: z.array(HistoryItem), truncated: z.boolean() })
      .nullable(),
  }),
});

// runs

export const runsStart = defineHostMethod("runs.start", {
  params: z.object({
    chatId: Id,
    /** Chosen by the agent. */
    runId: Id,
    /** Highest seq of the inputs this run reads first. */
    throughSeq: z.number().int().min(0),
    sessionId: Id.nullable(),
    agentKind: AgentKind,
    model: z.string().max(200).nullable(),
    /** The spec revision the run uses. */
    revision: z.number().int().min(0),
    /** A compaction run (no input is read). */
    compaction: z.boolean(),
    /**
     * The interrupted run of this chat this run continues. Such a run may read
     * nothing new (`throughSeq` equal to the read position): it resumes the
     * interrupted run's branch. Not once a run that read input started after
     * the interrupted one: then the start is refused unless it reads something.
     */
    continues: Id.nullable().optional(),
  }),
  result: z.object({ readSeq: z.number().int().min(0) }),
});

export const runsRead = defineHostMethod("runs.read", {
  params: z.object({ runId: Id, throughSeq: z.number().int().min(0) }),
  result: Ok,
});

export const runsProgress = defineHostMethod("runs.progress", {
  params: z.object({ runId: Id, live: RunLive }),
  result: Ok,
});

export const runsEnd = defineHostMethod("runs.end", {
  params: z.object({
    runId: Id,
    end: RunEnd,
    /** The notice to the parent chat that the run ended, signed by the agent. */
    notice: signedShape.nullable().optional(),
  }),
  result: Ok,
});

// replies

export const repliesStream = defineHostMethod("replies.stream", {
  params: z.object({
    runId: Id,
    /** The entry already written for this segment, if any. */
    entryId: Id.nullable(),
    /**
     * The whole text of the current segment so far. No longer than a reply
     * may be stored: every screen of the owner gets it as it is.
     */
    text: z.string().max(REPLY_TEXT_MAX),
  }),
  result: Ok,
});

export const repliesWrite = defineHostMethod("replies.write", {
  params: z.object({
    runId: Id,
    /** null creates the segment's entry. */
    entryId: Id.nullable(),
    text: z.string(),
    /** The segment is finished (cut at an injection, or the run's last). */
    final: z.boolean(),
    changes: z.array(Change).max(CHANGES_MAX).nullable(),
  }),
  result: z.object({ entryId: Id }),
});

export const chatsTitle = defineHostMethod("chats.title", {
  params: z.object({
    chatId: Id,
    title: z.string().min(1).max(200),
    /** Replace a title already set (the agent renaming its chat on request). */
    replace: z.boolean().optional(),
  }),
  /** applied is false when the chat already had a title and replace is not set. */
  result: z.object({ applied: z.boolean(), title: z.string().nullable() }),
});

// MCP

export const mcp = defineHostMethod("mcp", {
  params: z.object({
    /** The run that called; writes are marked with it. */
    runId: Id,
    chatId: Id,
    /** One JSON-RPC message from the CLI. */
    message: z.unknown(),
    /**
     * What the agent signed for the call (a child's conditions and first
     * words, an instruction to a child, a delegation): the store keeps them
     * with the rows the call writes.
     */
    signed: z.array(signedShape).max(4).optional(),
  }),
  /** The JSON-RPC response; null for a notification. */
  result: z.object({ response: z.unknown() }),
});

// turn items

const Position = z.enum(TURN_POSITIONS);

export const turnAdd = defineHostMethod("turn.add", {
  params: z.object({
    chatId: Id,
    runId: Id.nullable(),
    spec: TurnSpec,
    position: Position,
    /** The notice to the parent chat (an item routed there), signed by the agent. */
    notice: signedShape.nullable().optional(),
  }),
  result: z.object({ item: TurnItem, open: z.number().int().min(0) }),
});

export const turnEdit = defineHostMethod("turn.edit", {
  params: z.object({
    chatId: Id,
    itemId: Id,
    spec: TurnSpec,
    reason: z.string().min(1).max(500),
    cause: z.enum(TURN_EDIT_CAUSES),
    version: z.number().int().min(0).nullable(),
  }),
  result: z.object({ item: TurnItem, outcome: z.enum(TURN_EDIT_OUTCOMES) }),
});

export const turnDone = defineHostMethod("turn.done", {
  params: z.object({
    chatId: Id,
    itemId: Id,
    note: z.string().min(1).max(500),
  }),
  result: TurnItem,
});

export const turnWithdraw = defineHostMethod("turn.withdraw", {
  params: z.object({
    chatId: Id,
    itemId: Id,
    reason: z.string().min(1).max(500),
    /** Withdrawing a child's item: what the agent signed for the child (`answer`). */
    signed: signedShape.nullable().optional(),
  }),
  result: TurnItem,
});

export const turnReorder = defineHostMethod("turn.reorder", {
  params: z.object({ chatId: Id, ids: z.array(Id).min(1).max(TURN_OPEN_MAX) }),
  result: z.object({ open: z.array(TurnItem) }),
});

export const turnList = defineHostMethod("turn.list", {
  params: ChatParams,
  result: z.object({
    /** Open items in order (the first is shown). */
    open: z.array(TurnItem),
    /** Recently closed items that leave a line, oldest first. */
    closed: z.array(TurnItem),
    /** Items of this chat's children waiting for this chat's decision. */
    awaiting: z.array(TurnItem),
  }),
});

export const turnGet = defineHostMethod("turn.get", {
  params: z.object({ chatId: Id, itemId: Id }),
  result: TurnItem,
});

export const turnRaise = defineHostMethod("turn.raise", {
  params: z.object({
    chatId: Id,
    itemId: Id,
    position: Position,
    /** The notice to the parent chat (the item raised there), signed by the agent. */
    notice: signedShape.nullable().optional(),
  }),
  result: TurnItem,
});

export const turnAnswerChild = defineHostMethod("turn.answerChild", {
  params: z.object({
    chatId: Id,
    itemId: Id,
    answer: TurnAnswer.omit({ fileIds: true }),
    /** The answer as the agent signed it for its child (`answer`). */
    signed: signedShape.nullable().optional(),
  }),
  result: TurnItem,
});

export const turnSlackDraftResult = defineHostMethod("turn.slackDraftResult", {
  params: z.object({ chatId: Id, itemId: Id, result: SlackDraftResult }),
  result: TurnItem,
});

// files

const FileMeta = z.object({
  name: z.string().min(1).max(500).nullable(),
  mime: z.string().max(200).nullable(),
});

/** Offline edition: the core reads the file from the same PC. */
export const filesPutPath = defineHostMethod("files.putPath", {
  params: z.object({ path: z.string().min(1).max(4000), ...FileMeta.shape }),
  result: FileRef,
});

/** Offline edition: the core writes the file to the path on the same PC. */
export const filesGetToPath = defineHostMethod("files.getToPath", {
  params: z.object({
    fileId: Id,
    path: z.string().min(1).max(4000),
    maxBytes: z.number().int().min(1),
  }),
  result: FileRef,
});

/** Cloud: reserve an upload and get a presigned PUT. */
export const filesBegin = defineHostMethod("files.begin", {
  params: z.object({
    name: z.string().min(1).max(500),
    /** Empty: the core decides from the name. */
    mime: z.string().max(200),
    size: z.number().int().min(0),
    /** The content's SHA-256 (hex). The core checks it on complete. */
    hash: z.string().regex(/^[0-9a-f]{64}$/),
  }),
  result: z.object({
    fileId: Id,
    /** null: the same content is already stored; call `files.complete` without a PUT. */
    url: z.string().max(8000).nullable(),
    headers: z.record(z.string(), z.string()),
  }),
});

/** Cloud: the PUT finished. */
export const filesComplete = defineHostMethod("files.complete", {
  params: z.object({ fileId: Id }),
  result: FileRef,
});

/** Cloud: a signed URL to download a file. */
export const filesUrl = defineHostMethod("files.url", {
  params: z.object({ fileId: Id }),
  result: z.object({ file: FileRef, url: z.string().max(8000) }),
});

// requests

export const requestsDone = defineHostMethod("requests.done", {
  params: z.object({ requestId: Id, result: RequestResult }),
  result: Ok,
});

// login

export const loginState = defineHostMethod("login.state", {
  params: z.object({
    kind: AgentKind,
    login: Login.nullable(),
    /** Set together on success so the button does not flash back. */
    auth: Auth.nullable(),
  }),
  result: Ok,
});

/** Every method of AgentHost. */
export const AGENT_HOST_METHODS = hostMethodTable(
  hello,
  capabilitiesPut,
  chatsPending,
  chatsWork,
  runsStart,
  runsRead,
  runsProgress,
  runsEnd,
  repliesStream,
  repliesWrite,
  chatsTitle,
  mcp,
  turnAdd,
  turnEdit,
  turnDone,
  turnWithdraw,
  turnReorder,
  turnList,
  turnGet,
  turnRaise,
  turnAnswerChild,
  turnSlackDraftResult,
  filesPutPath,
  filesGetToPath,
  filesBegin,
  filesComplete,
  filesUrl,
  requestsDone,
  loginState,
);
export type AgentHostMethods = typeof AGENT_HOST_METHODS;
export type AgentHostMethodName = keyof AgentHostMethods;

/** Params a method takes (before parsing, so defaults may be left out). */
export type ParamsOf<T extends HostMethodTable, M extends keyof T> = z.input<
  T[M]["params"]
>;
/** What a method returns (parsed). */
export type ResultOf<T extends HostMethodTable, M extends keyof T> = z.output<
  T[M]["result"]
>;
